import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Link,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MaintenanceRecord, MaintenanceEvent, MaintenanceDocument } from '../../types/maintenance';
import { Vehicle } from '../../types/vehicle';
import { Supplier } from '../../types/supplier';
import { getMaintenanceRecord } from '../../services/maintenanceService';
import { formatDate } from '../../utils/dateUtils';
import vehicleService from '../../services/vehicleService';
import { getSupplier } from '../../services/supplierService';
import TabPanel from '../../components/common/TabPanel';
import { Download as DownloadIcon, Edit as EditIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const MaintenanceRecordDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [record, setRecord] = useState<MaintenanceRecord | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('maintenance.errors.invalidId');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel
        const maintenanceData = await getMaintenanceRecord(id);
        if (!maintenanceData) {
          throw new Error('maintenance.errors.recordNotFound');
        }

        const vehicleData = await vehicleService.getVehicle(maintenanceData.vehicleId);
        if (!vehicleData) {
          throw new Error('maintenance.errors.vehicleNotFound');
        }

        // Set the main data
        setRecord(maintenanceData);
        setVehicle(vehicleData);

        // Try to fetch supplier if available (optional)
        if (maintenanceData.serviceProviderId) {
          try {
            const supplierData = await getSupplier(maintenanceData.serviceProviderId);
            setSupplier(supplierData);
          } catch (error) {
            console.error('Error fetching supplier:', error);
            // Don't block for supplier errors
          }
        }
      } catch (error: any) {
        console.error('Error in fetchData:', error);
        const errorKey = error.message || 'maintenance.errors.loadFailed';
        setError(errorKey);
        setRecord(null);
        setVehicle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    navigate(`/admin/maintenance/${id}/edit`);
  };

  type StatusColor = 'warning' | 'info' | 'success' | 'error' | 'default';
  
  const getStatusColor = (status: string): StatusColor => {
    const colors: Record<string, StatusColor> = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const renderGeneralInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" color="textSecondary">
          {t('maintenance.vehicle')}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {vehicle ? `${vehicle.make} ${vehicle.model} - ${vehicle.licensePlate}` : '-'}
        </Typography>

        <Typography variant="subtitle1" color="textSecondary">
          {t('maintenance.serviceProvider')}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {supplier ? supplier.name : '-'}
        </Typography>

        <Typography variant="subtitle1" color="textSecondary">
          {t('maintenance.openDate')}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {formatDate(record?.openDate)}
        </Typography>

        <Typography variant="subtitle1" color="textSecondary">
          {t('maintenance.status')}
        </Typography>
        <Chip 
          label={t(`maintenance.statusTypes.${record?.status}`)}
          color={getStatusColor(record?.status || '')}
          sx={{ my: 1 }}
        />

        <Typography variant="subtitle1" color="textSecondary">
          {t('maintenance.type')}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {t(`maintenance.types.${record?.type}`)}
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" color="textSecondary">
          {t('maintenance.closeDate')}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {record?.closeDate ? formatDate(record.closeDate) : t('maintenance.notClosed')}
        </Typography>

        <Typography variant="subtitle1" color="textSecondary">
          {t('maintenance.costHT')}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {record?.costHT ? `${record.costHT.toFixed(2)} €` : '-'}
        </Typography>

        <Typography variant="subtitle1" color="textSecondary">
          {t('maintenance.costTTC')}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {record?.costTTC ? `${record.costTTC.toFixed(2)} €` : '-'}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" color="textSecondary">
          {t('maintenance.description')}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {record?.description || '-'}
        </Typography>
      </Grid>
    </Grid>
  );

  const renderDetailedOperations = () => (
    <Grid container spacing={3}>
      {record?.detailedOperations?.parts && record.detailedOperations.parts.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            {t('maintenance.parts')}
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('maintenance.partName')}</TableCell>
                  <TableCell align="right">{t('maintenance.quantity')}</TableCell>
                  <TableCell align="right">{t('maintenance.unitPrice')}</TableCell>
                  <TableCell align="right">{t('maintenance.totalPrice')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {record.detailedOperations.parts.map((part, index) => (
                  <TableRow key={index}>
                    <TableCell>{part.name}</TableCell>
                    <TableCell align="right">{part.quantity || '-'}</TableCell>
                    <TableCell align="right">
                      {part.unitPrice ? `${part.unitPrice.toFixed(2)} €` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {part.quantity && part.unitPrice
                        ? `${(part.quantity * part.unitPrice).toFixed(2)} €`
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      {record?.detailedOperations?.labor && record.detailedOperations.labor.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            {t('maintenance.labor')}
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('maintenance.description')}</TableCell>
                  <TableCell align="right">{t('maintenance.hours')}</TableCell>
                  <TableCell align="right">{t('maintenance.cost')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {record.detailedOperations.labor.map((labor, index) => (
                  <TableRow key={index}>
                    <TableCell>{labor.description}</TableCell>
                    <TableCell align="right">{labor.hours || '-'}</TableCell>
                    <TableCell align="right">
                      {labor.cost ? `${labor.cost.toFixed(2)} €` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}
    </Grid>
  );

  const renderDocuments = () => (
    <List>
      {record?.documents.map((doc: MaintenanceDocument) => (
        <React.Fragment key={doc.id}>
          <ListItem
            secondaryAction={
              <IconButton edge="end" aria-label="download" href={doc.url} target="_blank">
                <DownloadIcon />
              </IconButton>
            }
          >
            <ListItemText
              primary={
                <Link href={doc.url} target="_blank" underline="hover">
                  {doc.name}
                </Link>
              }
              secondary={`${t(`maintenance.documentTypes.${doc.type}`)} - ${formatDate(
                doc.uploadedAt
              )}`}
            />
          </ListItem>
          <Divider />
        </React.Fragment>
      ))}
    </List>
  );

  const renderHistory = () => (
    <List>
      {record?.events.map((event: MaintenanceEvent) => (
        <React.Fragment key={event.id}>
          <ListItem>
            <ListItemText
              primary={event.description}
              secondary={`${formatDate(event.date)} - ${event.type}`}
            />
          </ListItem>
          <Divider />
        </React.Fragment>
      ))}
    </List>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !record || !vehicle) {
    return (
      <Box p={3}>
        <Typography color="error" gutterBottom>
          {t(error || 'maintenance.recordNotFound')}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/admin/maintenance')}
          >
            {t('common.backToList')}
          </Button>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => window.location.reload()}
          >
            {t('common.retry')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('maintenance.recordDetails')}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={handleEdit}
            startIcon={<EditIcon />}
            sx={{ mr: 2 }}
          >
            {t('common.edit')}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/admin/maintenance')}>
            {t('common.back')}
          </Button>
        </Box>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="maintenance record tabs">
            <Tab label={t('maintenance.tabs.general')} />
            <Tab label={t('maintenance.tabs.operations')} />
            <Tab label={t('maintenance.tabs.documents')} />
            <Tab label={t('maintenance.tabs.history')} />
          </Tabs>
        </Box>

        <CardContent>
          <TabPanel value={tabValue} index={0}>
            {renderGeneralInfo()}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {renderDetailedOperations()}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            {renderDocuments()}
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            {renderHistory()}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MaintenanceRecordDetailPage;
